import React, { useEffect, useState, useContext } from "react";
import moment, { Moment } from "moment";
import { message, DatePicker } from "antd";
import {
  getNumOfHourMin,
  getFormatByMill,
  getStartTimeOfDayByMill,
  getRangArr,
  getWholeMins,
} from "./utils";
import locale from "./locale";
import EnhanceTip from "./common/enhanceTip";
import eventBus from "./utils/events";
import { ToolFnContext } from "./utils/context";
import { getDefaultComponentStyle } from "./utils/defaultStyles";

export default ({
  data,
  getText,
  setRetrieveValues,
  getRelationTime,
  btnRequestHandle,
  setSubmitState,
  isStreaming,
}: {
  data: IMultiElementCombineMsg.IDatePickerElem;
  getText: (text: IMultiElementCombineMsg.IText) => string;
  setRetrieveValues: (value: IMultiElementCombineMsg.IRetrieveValues) => void;
  getRelationTime: (id: string) => string;
  btnRequestHandle: ({
    url,
    elem,
    extra,
  }: IMultiElementCombineMsg.ISubmitParams) => void;
  setSubmitState: (tipCheck: IMultiElementCombineMsg.ITipCheck) => void;
  isStreaming: boolean;
}): JSX.Element | null => {
  const {
    id,
    type,
    title,
    format = "YYYY-MM-DD HH:mm",
    default_time,
    disable,
    required,
    required_tips,
    prompt_tips,
    relation_type,
    relation_id,
    relation_tips,
    action_url,
    extra,
    begin_time_limit,
    end_time_limit,
    placeholder_title,
  } = data;
  const style = getDefaultComponentStyle(data);
  const [dateValue, setDateValue] = useState(() => {
    let value: any = null;
    if (default_time) {
      const stamp = getWholeMins(default_time);
      value = moment(stamp * 1000);
    }
    return value;
  });
  const [relationTip, setRelationTip] = useState("");
  const [disabledTime, setDisabledTime] = useState<any>(() => ({
    disabledHours: () => [],
    disabledMinutes: () => [],
  }));
  const { msgId } = useContext(ToolFnContext);

  useEffect(() => {
    // 日期组件有依赖项时，必须互相依赖
    if (!!disable || !relation_id) return;

    const refreshFn = (relationId: string) => {
      if (relationId === `${msgId}-${id}`) setRelationTip("");
    };
    eventBus.on("refreshDateTip", refreshFn);
    return () => {
      eventBus.off("refreshDateTip", refreshFn);
    };
  }, []);

  useEffect(() => {
    if (!default_time) return setDateValue(null);

    const stamp = getWholeMins(default_time);
    setDateValue(moment(stamp * 1000));
  }, [default_time]);

  useEffect(() => {
    const value = dateValue ? dateValue.format("X") : "";
    setRetrieveValues({ id, type, value });
    // relation time，相关联的日期组件变更同步
    if (relation_id && relation_type && relation_tips) {
      const relationTime = getRelationTime ? getRelationTime(relation_id) : "";
      if (!value || !relationTime) {
        return setRelationTip("");
      }

      let isTip = false;
      if (relation_type === "before") {
        isTip = +value >= +relationTime;
      } else if (relation_type === "after") {
        isTip = +value <= +relationTime;
      }
      isTip ? setRelationTip(getText(relation_tips)) : setRelationTip("");
      !isTip && eventBus.emit("refreshDateTip", `${msgId}-${relation_id}`);
    }
  }, [dateValue, relation_id, relation_type, relation_tips]);

  const getDisabledDate = (current: any) => {
    if (!current) return false;

    const beginDisableTime = begin_time_limit
      ? current < moment(begin_time_limit * 1000).startOf("day")
      : false;
    const endDisableTime = end_time_limit
      ? current > moment(end_time_limit * 1000).endOf("day")
      : false;

    return beginDisableTime || endDisableTime;
  };

  const getTimeArr = (stamp: number | string) =>
    getNumOfHourMin(getFormatByMill(stamp));

  const onSelect = (val: Moment | null) => {
    if (!val || !begin_time_limit || !end_time_limit) return;
    const currentStamp = moment(val).format("X");
    const currentStartStamp = getStartTimeOfDayByMill(currentStamp);
    const beginLimitStartStamp = getStartTimeOfDayByMill(begin_time_limit);
    const endLimitStartStamp = getStartTimeOfDayByMill(end_time_limit);
    const [selectedHour] = getTimeArr(currentStamp);

    let hours: any[] = [],
      mins: any[] = [];
    if (
      currentStartStamp < beginLimitStartStamp ||
      currentStartStamp > endLimitStartStamp
    ) {
      hours = getRangArr(0, 23);
      mins = getRangArr(0, 59);
    }
    if (currentStartStamp === beginLimitStartStamp) {
      const [h, m] = getTimeArr(begin_time_limit);
      hours = getRangArr(0, h - 1);
      if (selectedHour === h) {
        mins = getRangArr(0, m - 1);
      } else if (selectedHour < h) {
        mins = getRangArr(0, 59);
      }
    }
    if (currentStartStamp === endLimitStartStamp) {
      const [h, m] = getTimeArr(end_time_limit);
      hours = [...hours, ...getRangArr(h + 1, 23)];
      if (selectedHour === h) {
        mins = [...mins, ...getRangArr(m + 1, 59)];
      } else if (selectedHour > h) {
        mins = getRangArr(0, 59);
      }
    }
    return setDisabledTime({
      disabledHours: () => hours,
      disabledMinutes: () => mins,
    });
  };

  const onChange = (val: Moment | null) => {
    const value = val ? val.format("X") : "";

    if (value) {
      // 时间选择器中精确到秒，省略秒数, 避免默认值导致一分钟内的误差
      if (begin_time_limit && +value < getWholeMins(begin_time_limit)) {
        message.error(locale("im_selected_invalid_date"));
        return;
      }
      if (end_time_limit && +value > getWholeMins(end_time_limit)) {
        message.error(locale("im_selected_invalid_date"));
        return;
      }
    }
    setDateValue(val);
    // setSession(idClient, { id, value }, mod_time);
    action_url &&
      btnRequestHandle({ url: action_url, elem: { id, type, value }, extra });
  };

  const onOpenChange = (open: boolean) => {
    open && onSelect(dateValue);
  };

  return (
    <div style={style}>
      <EnhanceTip
        id={id}
        isEmpty={!dateValue}
        required={required}
        required_tips={required_tips}
        prompt_tips={prompt_tips}
        relationTip={relationTip}
        isLeftGap={!!getText(title)}
        setSubmitState={setSubmitState}
      >
        <div
          className={"sscard-flexBox"}
          id={`dateElem-${id}`}
          onClick={(e) => e.stopPropagation()}
        >
          {getText(title) && <span className={"desc"}>{getText(title)}</span>}
          <DatePicker
            className={"selectItem"}
            disabled={isStreaming || !!disable}
            disabledDate={getDisabledDate}
            disabledTime={() => disabledTime}
            value={dateValue}
            showTime={format.includes("HH:mm")}
            format={
              format.includes("HH:mm") ? "YYYY-MM-DD HH:mm" : "YYYY-MM-DD"
            }
            onChange={onChange}
            onClick={(e) => e.stopPropagation()}
            onSelect={onSelect}
            getPopupContainer={() =>
              document.getElementById(`dateElem-${id}`) as HTMLElement
            }
            // showNow={false}
            allowClear={false}
            onOpenChange={onOpenChange}
            placeholder={
              getText(placeholder_title) || locale("please_select_time")
            }
          />
        </div>
      </EnhanceTip>
    </div>
  );
};
